import React, { useState, useEffect } from 'react';
import { Smartphone, Tablet, Monitor, Maximize } from 'lucide-react';

interface BreakpointTest {
  name: string;
  width: string;  
  minWidth: number;
  maxWidth?: number;
  active: boolean;
}

const ResponsiveTest: React.FC = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  const breakpoints: BreakpointTest[] = [
    { name: 'Mobile (sm)', width: '< 640px', minWidth: 0, maxWidth: 639, active: screenWidth < 640 },
    { name: 'Tablet (md)', width: '640px - 767px', minWidth: 640, maxWidth: 767, active: screenWidth >= 640 && screenWidth < 768 },
    { name: 'Tablet L (lg)', width: '768px - 1023px', minWidth: 768, maxWidth: 1023, active: screenWidth >= 768 && screenWidth < 1024 },
    { name: 'Desktop (xl)', width: '1024px - 1279px', minWidth: 1024, maxWidth: 1279, active: screenWidth >= 1024 && screenWidth < 1280 },
    { name: 'Desktop XL (2xl)', width: '≥ 1280px', minWidth: 1280, active: screenWidth >= 1280 },
  ];

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getBreakpointIcon = (breakpoint: BreakpointTest) => {
    if (breakpoint.minWidth < 640) return <Smartphone className="h-5 w-5" />;
    if (breakpoint.minWidth < 1024) return <Tablet className="h-5 w-5" />;
    return <Monitor className="h-5 w-5" />;
  };

  const testUrls = [
    { name: 'Home Page', path: '/' },
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'Groups', path: '/groups' },
    { name: 'Profile', path: '/profile' },
    { name: 'Group Chat', path: '/groups/1/chat' },
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Maximize className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            Responsive Design Test
          </h2>
        </div>

        {/* Current Screen Info */}
        <div className="mb-6 p-4 bg-blue-50 rounded-xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-blue-900">
                {screenWidth}px
              </div>
              <div className="text-sm text-blue-600">Width</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-blue-900">
                {screenHeight}px
              </div>
              <div className="text-sm text-blue-600">Height</div>
            </div>
            <div className="col-span-2 md:col-span-2">
              <div className="text-lg font-semibold text-blue-900">
                {breakpoints.find(bp => bp.active)?.name || 'Unknown'}
              </div>
              <div className="text-sm text-blue-600">Current Breakpoint</div>
            </div>
          </div>
        </div>

        {/* Breakpoint Status */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Tailwind CSS Breakpoints
          </h3>
          <div className="space-y-2">
            {breakpoints.map((breakpoint, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                  breakpoint.active 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-gray-50 text-gray-600'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {getBreakpointIcon(breakpoint)}
                  <span className="font-medium">{breakpoint.name}</span>
                  <span className="text-sm opacity-75">({breakpoint.width})</span>
                </div>
                {breakpoint.active && (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium">Active</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Common Test Resolutions */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Common Device Resolutions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { device: 'iPhone SE', width: '375x667' },
              { device: 'iPhone 12', width: '390x844' },
              { device: 'iPad', width: '768x1024' },
              { device: 'iPad Pro', width: '1024x1366' },
              { device: 'Desktop HD', width: '1920x1080' },
              { device: 'Desktop 4K', width: '3840x2160' },
            ].map((device, index) => (
              <div
                key={index}
                className="p-3 bg-gray-50 rounded-xl text-center"
              >
                <div className="font-medium text-gray-900">{device.device}</div>
                <div className="text-sm text-gray-600">{device.width}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Test Components */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Test Components
          </h3>
          
          {/* Sample responsive grid */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Responsive Grid Test</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="p-4 bg-indigo-100 rounded-xl text-center"
                >
                  <div className="text-indigo-900 font-medium">Item {item}</div>
                  <div className="text-xs text-indigo-600 mt-1">
                    <span className="sm:hidden">1 col</span>
                    <span className="hidden sm:inline md:hidden">2 cols</span>
                    <span className="hidden md:inline lg:hidden">3 cols</span>
                    <span className="hidden lg:inline">4 cols</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sample responsive text */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Typography Test</h4>
            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                Responsive Heading
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-gray-600">
                This text adapts to different screen sizes using responsive typography classes.
              </p>
            </div>
          </div>

          {/* Sample responsive spacing */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Spacing Test</h4>
            <div className="p-2 sm:p-4 md:p-6 lg:p-8 bg-yellow-100 rounded-xl">
              <div className="text-yellow-900">
                Padding: p-2 sm:p-4 md:p-6 lg:p-8
              </div>
            </div>
          </div>
        </div>

        {/* Quick Test Links */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Quick Page Tests
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {testUrls.map((url, index) => (
              <a
                key={index}
                href={url.path}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-sm text-center text-indigo-600 hover:text-indigo-500 hover:bg-indigo-50 border border-indigo-200 rounded-xl transition-colors"
              >
                {url.name}
              </a>
            ))}
          </div>
        </div>

        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h4 className="text-sm font-medium text-amber-800 mb-2">Testing Instructions:</h4>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• Resize your browser window to test different breakpoints</li>
            <li>• Use browser dev tools to simulate mobile devices</li>
            <li>• Check that all elements remain usable at different sizes</li>
            <li>• Verify text remains readable without horizontal scrolling</li>
            <li>• Test both portrait and landscape orientations on mobile</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveTest;